import React, { useEffect, useContext, useCallback } from 'react';
import { PAGED_REPORTS_QUERY } from './queries';
import { REPORT_DELETED } from './subscriptions';
import { ServiceContext } from 'providers/ServiceProvider';
import { useTranslation } from 'react-i18next';
import { Card } from '@totalsoft/rocket-ui';
import ExportAction from './actions/ExportAction';
import { useApolloClient } from '@apollo/client';
import { getListBrowserStore, useListBrowserUtils } from '@ts-private/list-browser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useToast } from 'hooks/toasts/useToast';
import { useMemo } from 'react';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import ReportItem from './ReportItem';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { useMutation } from 'hooks/apollo/useMutation';
import { DELETE_REPORT } from './mutations';
import useSubscription from 'hooks/apollo/useSubscription';

export const reportsStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'reportName',
      caption: 'Reports.ReportName',
      type: 'text'
    },
    {
      name: 'reportFriendlyName',
      caption: 'Reports.ReportFriendlyName',
      type: 'text'
    }
  ],
  orderByFields: [
    {
      caption: 'Reports.ReportName',
      value: 'ReportName'
    },
    {
      caption: 'Reports.ReportFriendlyName',
      value: 'ReportFriendlyName'
    }
  ],
  pager: {
    orderBy: 'ReportName',
    direction: 'asc'
  }
});

const ReportListBrowser = () => {
  const [t] = useTranslation();
  const addToast = useToast();
  const client = useApolloClient();
  const [activeService] = useContext(ServiceContext);
  const confirm = useConfirmDialog();
  const { addActions } = useListBrowserUtils(reportsStore);

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: PAGED_REPORTS_QUERY,
        name: 'pagedReports'
      }
    }),
    []
  );

  // #region Delete

  useSubscription(REPORT_DELETED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('Reports.ReportDeleted'), 'success');
      client.cache.evict({ id: `ReportItem:{"reportId":${data.reportDeleted.reportId}}` });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedReports' });
      client.cache.gc();
    }
  });

  const [deleteReport] = useMutation(DELETE_REPORT);

  const handleDelete = useCallback(
    reportId => {
      confirm(
        () => {
          deleteReport({ variables: { input: { reportId: reportId } } });
        },
        {
          content: t('Reports.DeleteReport')
        }
      );
    },
    [deleteReport, confirm, t]
  );

  // #endregion

  useEffect(() => {
    if (activeService) {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedReports' });
      client.cache.gc();
    }
  }, [activeService, client]);

  // TODO Wrong way to add actions to list-browser!! Will need a refactor
  useEffect(() => {
    addActions([
      {
        name: 'export',
        icon: <ExportAction />
      }
    ]);
  }, [addActions]);

  const itemComponent = useCallback(({ data }) => <ReportItem data={data} onDelete={handleDelete} />, [handleDelete]);

  const columns = useMemo(
    () => [
      { name: 'reportName', caption: 'Reports.ReportName' },
      { name: 'reportFriendlyName', caption: 'Reports.ReportFriendlyName' },
      { name: 'actions', caption: 'Actions', width: 15 }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser name='reports-verification-browser' store={reportsStore} ItemComponent={itemComponent} columns={columns} />
      <ApolloFetchingComponent store={reportsStore} options={fetchingOptions} />
    </Card>
  );
};

export default ReportListBrowser;
